import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Ban,
  ChevronRight,
  CircleHelp,
  FileText,
  Image as ImageIcon,
  Languages,
  MessageSquareHeart,
  UserRoundX,
} from 'lucide-react';
import {
  APP_VERSION,
  BRAND,
  SETTINGS_COPY,
  TRANSLATE_COPY,
  TRANSLATE_LANGUAGES,
  UPLOAD_QUALITY_OPTIONS,
  UPLOAD_SETTINGS_COPY,
  type TranslateLanguageCode,
  type UploadQualityCode,
} from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Card, SectionTitle, Switch } from '@/components/ui';
import { apiData } from '@/lib/api';
import { getAppSettings, setAppSettings, type AppSettings } from '@/lib/app-settings';

/** 앱 설정 허브 (v1.18) — 기기 설정·서비스 이용·정책이 한 화면에 모인다 */
export function SettingsPage() {
  const [version, setVersion] = useState<string | null>(null);

  // 네이티브 앱에서만 버전이 잡힌다 — 웹은 항상 최신이라 표기를 달리한다
  useEffect(() => {
    void (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        const { App } = await import('@capacitor/app');
        const info = await App.getInfo();
        setVersion(`v${info.version} (${info.build})`);
      } catch {
        /* 웹 폴백 — 아래에서 처리 */
      }
    })();
  }, []);

  return (
    <Screen>
      <AppBar title={SETTINGS_COPY.title} back />

      <div className="p-4">
        <SectionTitle>{SETTINGS_COPY.groupUser}</SectionTitle>
        <Card className="p-0">
          <SettingsRow to="/settings/uploads" icon={<ImageIcon size={18} />} label={SETTINGS_COPY.uploads} />
          <SettingsRow to="/settings/translation" icon={<Languages size={18} />} label={SETTINGS_COPY.translation} />
          <SettingsRow to="/settings/blocks" icon={<Ban size={18} />} label={SETTINGS_COPY.blocks} />
        </Card>

        <SectionTitle>{SETTINGS_COPY.groupService}</SectionTitle>
        <Card className="p-0">
          <SettingsRow to="/settings/feedback" icon={<MessageSquareHeart size={18} />} label={SETTINGS_COPY.feedback} />
          <SettingsRow to="/help" icon={<CircleHelp size={18} />} label={SETTINGS_COPY.help} />
        </Card>

        <SectionTitle>{SETTINGS_COPY.groupEtc}</SectionTitle>
        <Card className="p-0">
          <SettingsRow to="/policies" icon={<FileText size={18} />} label={SETTINGS_COPY.policies} />
          <SettingsRow
            to="/settings/delete-account"
            icon={<UserRoundX size={18} />}
            label={SETTINGS_COPY.accountDelete}
          />
          <div className="flex items-center justify-between px-4 py-3.5">
            <span className="text-base">{SETTINGS_COPY.versionLabel}</span>
            <span className="text-sm text-ink-sub">
              {version ?? `v${APP_VERSION} · ${BRAND.name} 웹`}
            </span>
          </div>
        </Card>
      </div>
    </Screen>
  );
}

function SettingsRow({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <Link
      to={to}
      className="tap flex items-center gap-3 border-b border-line px-4 py-3.5 last:border-b-0"
    >
      <span className="grid h-9 w-9 place-items-center rounded-full bg-primary-soft text-primary-strong">
        {icon}
      </span>
      <span className="flex-1 text-base">{label}</span>
      <ChevronRight size={18} className="text-ink-sub" />
    </Link>
  );
}

/** 사진 업로드 설정 — Wi-Fi 확인 + 화질 (v1.18) */
export function UploadSettingsPage() {
  const [settings, setSettings] = useState<AppSettings>(() => getAppSettings());

  const update = (patch: Partial<AppSettings>) => setSettings(setAppSettings(patch));

  return (
    <Screen>
      <AppBar title={SETTINGS_COPY.uploads} back />

      <div className="p-4">
        <Card className="flex items-center justify-between gap-3">
          <div>
            <p className="text-base font-semibold">{UPLOAD_SETTINGS_COPY.wifiTitle}</p>
            <p className="mt-0.5 text-sm text-ink-sub">{UPLOAD_SETTINGS_COPY.wifiSub}</p>
          </div>
          <Switch
            checked={settings.uploadWifiCheck}
            onChange={(next) => update({ uploadWifiCheck: next })}
            label={UPLOAD_SETTINGS_COPY.wifiTitle}
          />
        </Card>
        <p className="mt-2 px-1 text-sm leading-relaxed text-ink-sub">
          {UPLOAD_SETTINGS_COPY.wifiNotice}
        </p>

        <SectionTitle>{UPLOAD_SETTINGS_COPY.qualityTitle}</SectionTitle>
        <Card className="p-0">
          {UPLOAD_QUALITY_OPTIONS.map((option) => (
            <label
              key={option.code}
              className="tap flex cursor-pointer items-center gap-3 border-b border-line px-4 py-3.5 last:border-b-0"
            >
              <input
                type="radio"
                name="upload-quality"
                className="h-4 w-4 accent-[var(--color-primary)]"
                checked={settings.uploadQuality === option.code}
                onChange={() => update({ uploadQuality: option.code as UploadQualityCode })}
              />
              <span className="text-base">{option.label}</span>
            </label>
          ))}
        </Card>
        <p className="mt-2 px-1 text-sm leading-relaxed text-ink-sub">
          {UPLOAD_SETTINGS_COPY.qualityNotice}
        </p>
      </div>
    </Screen>
  );
}

/** 번역 기능 설정 — 다문화가정 지원 (§5.28, v1.18) */
export function TranslationSettingsPage() {
  const [settings, setSettings] = useState<AppSettings>(() => getAppSettings());
  const update = (patch: Partial<AppSettings>) => setSettings(setAppSettings(patch));

  const status = useQuery({
    queryKey: ['ai', 'translate', 'status'],
    queryFn: () => apiData<{ available: boolean }>('/ai/translate/status'),
    staleTime: 5 * 60_000,
  });
  const available = status.data?.available ?? false;

  return (
    <Screen>
      <AppBar title={TRANSLATE_COPY.title} back />

      <div className="p-4">
        <Card className="flex items-center justify-between gap-3">
          <p className="text-base font-semibold">{TRANSLATE_COPY.toggleTitle}</p>
          <Switch
            checked={settings.translateEnabled && available}
            onChange={(next) => update({ translateEnabled: next })}
            label={TRANSLATE_COPY.toggleTitle}
          />
        </Card>
        <p className="mt-2 px-1 text-sm leading-relaxed text-ink-sub">
          {status.isSuccess && !available ? TRANSLATE_COPY.unavailable : TRANSLATE_COPY.notice}
        </p>

        {settings.translateEnabled && available && (
          <>
            <SectionTitle>{TRANSLATE_COPY.langLabel}</SectionTitle>
            <Card className="p-0">
              {TRANSLATE_LANGUAGES.map((lang) => (
                <label
                  key={lang.code}
                  className="tap flex cursor-pointer items-center gap-3 border-b border-line px-4 py-3 last:border-b-0"
                >
                  <input
                    type="radio"
                    name="translate-lang"
                    className="h-4 w-4 accent-[var(--color-primary)]"
                    checked={settings.translateLang === lang.code}
                    onChange={() => update({ translateLang: lang.code as TranslateLanguageCode })}
                  />
                  <span className="text-base">{lang.label}</span>
                </label>
              ))}
            </Card>
          </>
        )}
      </div>
    </Screen>
  );
}
